import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Component, Inject, OnInit, Output, EventEmitter, Input, ViewChildren, QueryList } from "@angular/core";
import { MatSnackBar } from "@angular/material";
import { remove, findIndex } from "lodash";
import { SimuladorFreteService } from "./../../services/simulador-frete.service";
import { SimuladorFreteProdutoComponent } from "../../components/produto/produto.component";
@Component({
  selector: "mandae-simulador-frete",
  templateUrl: "./simulador-frete.component.html",
  styleUrls: ["./simulador-frete.component.scss"],
})
export class SimuladorFreteComponent implements OnInit {
  public formulario: FormGroup;

  public produtos = [];

  public simulador = [];

  public loading = false;

  public simulando = false;

  public resultado = [];

  @ViewChildren(SimuladorFreteProdutoComponent)
  private readonly produtosComponents: QueryList<SimuladorFreteProdutoComponent>;

  @Input()
  public hideResults = false;

  @Input()
  public cepPredefinido: string;

  @Input()
  public makeEditableCep = false;

  @Input()
  public showResultsModal = false;

  @Input()
  public isSimulatingModal = false;

  @Output()
  public simulationComplete = new EventEmitter<any>();

  @Output()
  public simulationStart = new EventEmitter<void>();

  @Output()
  public productAdded = new EventEmitter<void>();

  constructor(
    @Inject(SimuladorFreteService) public simuladorFreteService,
    private readonly formBuilder: FormBuilder,
    private readonly snackBar: MatSnackBar
  ) {
    this.formulario = this.formBuilder.group({
      cepDestino: [null, Validators.required],
    });
  }

  public ngOnInit() {
    this.getToken();

    // Se CEP foi predefinido, preencher o campo
    if (this.cepPredefinido) {
      this.formulario.patchValue({
        cepDestino: this.cepPredefinido
      });

      // Desabilitar apenas se não for para tornar editável
      if (!this.makeEditableCep) {
        this.formulario.get('cepDestino').disable();
      }

      // Na modal, garantir que APENAS um produto seja exibido inicialmente
      this.produtos = []; // Limpar produtos existentes
      // O produto inicial já está definido no template como [Initial]="true"

      // Limpar resultado anterior
      this.resultado = [];
    } else {
      // Na tela normal, também exibir apenas um produto inicialmente
      this.produtos = []; // Limpar produtos existentes
      // O produto inicial já está definido no template como [Initial]="true"
    }

    // Observar mudanças no formulário principal para revalidar o botão
    this.formulario.valueChanges.subscribe(() => {
      // Trigger change detection for button validation
      setTimeout(() => {
        // Force change detection after a micro-task to ensure UI updates
      }, 0);
    });
  }

  public async getToken() {
    try {
      this.loading = true;
      const data = await this.simuladorFreteService.getToken();

      // Armazenar o token da API (token da tabela de parceiro) para uso no interceptor
      if (data && data.token) {
        localStorage.setItem("ls.token.app", JSON.stringify(data.token));
        console.log('Token de API carregado para simulador de frete');
      } else {
        console.warn('Token de API não encontrado na resposta');
      }

      this.loading = false;
    } catch (error) {
      console.error('Erro ao carregar token de API:', error);
      this.loading = false;
    }
  }

  public responseEventEmitter(event) {
    switch (event.action) {
      case "adicionar": {
        const indice = findIndex(this.simulador, { indice: event.data.indice });
        if (indice !== -1) {
          this.simulador[indice] = event.data;
        } else {
          this.simulador.push(event.data);
        }
        break;
      }
      case "remover":
        if (!event.data.initial) {
          this.produtos.splice(event.data.indice, 1);
          remove(this.simulador, {
            indice: event.data.indice,
          });
        }
        break;
      case "change":
        // Produto foi alterado, trigger change detection para revalidar botão
        // Não é necessário fazer nada específico, o verificarBotao() será chamado automaticamente
        break;
    }
  }

  public adicionarProduto(): void {
    this.produtos.push(`produto-${this.produtos.length}`);
    // Notificar containers (ex.: dialog) para ajustar scroll
    this.productAdded.emit();
  }

  public verificarBotao() {
    // Bloqueia se estiver simulando
    if (this.simulando) {
      return true;
    }

    // Bloqueia se CEP não estiver preenchido
    const cepControl = this.formulario.get('cepDestino');
    if (!cepControl || !cepControl.value || cepControl.value.trim() === '') {
      return true;
    }

    // Verifica se pelo menos um produto está válido e preenchido
    if (!this.produtosComponents || !this.produtosComponents.length) {
      return true;
    }

    const hasValidProduct = this.produtosComponents.some(produto => {
      const form = produto.formulario;
      if (!form) {
        return false;
      }

      // Verifica se todos os campos obrigatórios estão preenchidos
      const peso = form.get('peso');
      const altura = form.get('altura');
      const largura = form.get('largura');
      const comprimento = form.get('comprimento');
      const quantidade = form.get('quantidade');
      const valor = form.get('valor');

      // Validação mais robusta dos campos
      const pesoValid = peso && peso.value !== null && peso.value !== undefined &&
                       peso.value.toString().trim() !== '' && this.toNumber(peso.value) > 0;
      const alturaValid = altura && altura.value !== null && altura.value !== undefined &&
                         altura.value.toString().trim() !== '' && parseInt(altura.value.toString(), 10) > 0;
      const larguraValid = largura && largura.value !== null && largura.value !== undefined &&
                          largura.value.toString().trim() !== '' && parseInt(largura.value.toString(), 10) > 0;
      const comprimentoValid = comprimento && comprimento.value !== null && comprimento.value !== undefined &&
                              comprimento.value.toString().trim() !== '' && parseInt(comprimento.value.toString(), 10) > 0;
      const quantidadeValid = quantidade && quantidade.value !== null && quantidade.value !== undefined &&
                             quantidade.value.toString().trim() !== '' && parseInt(quantidade.value.toString(), 10) > 0;

      // Valor declarado é opcional - só valida se preenchido
      const valorValid = !valor || !valor.value || valor.value.toString().trim() === '' || this.toNumber(valor.value) > 0;

      // Todos os campos obrigatórios devem estar preenchidos (valor é opcional)
      const allFieldsValid = pesoValid && alturaValid && larguraValid && comprimentoValid && quantidadeValid && valorValid;

      // Não exigir form.valid para permitir habilitação mesmo com warnings não bloqueantes
      return allFieldsValid;
    });

    return !hasValidProduct;
  }

  public async calcularFrete() {
    try {
      // Garantir que o Angular processe os últimos eventos de input antes de ler os formulários
      await this.flushFormUpdates();

      // Ler valores atuais dos formulários DOS PRODUTOS antes de qualquer mudança de estado
      const itensBrutos = this.buildItemsFromForms();

      // Validar se há ao menos um item COMPLETO e VÁLIDO preenchido
      const itensPreenchidos = (itensBrutos || []).filter((item) => {
        if (!item) { return false; }
        const w = this.toNumber(item.weight);
        const h = this.toInt(item.height);
        const wi = this.toInt(item.width);
        const le = this.toInt(item.length);
        const q = this.toInt(item.quantity);
        const v = this.toNumber(item.declaredValue);
        // Valor declarado é opcional - só valida se for maior que 0 quando preenchido
        return w > 0 && h > 0 && wi > 0 && le > 0 && q > 0 && (v === 0 || v > 0);
      });
      if (!itensPreenchidos || itensPreenchidos.length === 0) {
        // Sem itens válidos: abortar silenciosamente (sem snackbar)
        this.simulando = false;
        return;
      }

      // Emitir evento de início da simulação para a modal (após capturar os valores)
      this.simulationStart.emit();
      this.simulando = true;

      // Usar CEP predefinido ou valor do formulário
      let cepDestino = this.cepPredefinido || this.formulario.get("cepDestino").value;

      // Remover formatação do CEP (manter apenas números)
      if (typeof cepDestino === 'string') {
        cepDestino = cepDestino.replace(/\D/g, '');
      }

      // Normalizar os itens para garantir números corretos (peso, dimensões, valor, quantidade)
      const itensNormalizados = itensPreenchidos
        .map((item) => this.sanitizarItem(item));

      console.log('Dados enviados para simulação (normalizados):', {
        cep: cepDestino,
        items: itensNormalizados
      });

      const response = await this.simuladorFreteService.get(
        cepDestino,
        itensNormalizados
      );
      this.resultado = response.data;
      this.simulando = false;

      // Emitir evento para componentes pai (usado em dialogs)
      this.simulationComplete.emit({
        volumes: itensNormalizados,
        resultado: this.resultado
      });
    } catch (error) {
      console.error("Erro ao calcular frete:", error);
      this.simulando = false;

      const message = this.extrairMensagemErroFrete(error);
      this.snackBar.open(message, 'Fechar', { duration: 6000, verticalPosition: 'top' });
    }
  }

  private buildItemsFromForms(): any[] {
    if (!this.produtosComponents || !this.produtosComponents.length) {
      return [];
    }
    return this.produtosComponents.toArray().map((comp, idx) => {
      const f = comp.formulario;
      return {
        weight: f.get('peso') ? f.get('peso').value : null,
        height: f.get('altura') ? f.get('altura').value : null,
        width: f.get('largura') ? f.get('largura').value : null,
        length: f.get('comprimento') ? f.get('comprimento').value : null,
        quantity: f.get('quantidade') ? f.get('quantidade').value : null,
        declaredValue: f.get('valor') ? f.get('valor').value : null,
        indice: comp.indice || (idx + 1),
        initial: idx === 0,
        isValid: f.valid
      };
    });
  }

  private async flushFormUpdates(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  /**
   * Converte strings com máscara/locale em números.
   * Suporta formatos: "1.234,56", "1234,56", "1,234.56", "1234.56", "R$ 1.234,56".
   */
  private toNumber(value: any): number {
    if (value === null || value === undefined) { return 0; }
    if (typeof value === 'number') { return value; }
    let text = String(value).trim();
    if (!text) { return 0; }
    // Remove prefixos como R$, espaços e quaisquer caracteres não numéricos exceto dígitos, vírgula e ponto
    text = text.replace(/[^0-9.,-]/g, '');
    // Se contém vírgula e ponto, assumir vírgula como decimal (pt-BR): remover pontos de milhar
    if (text.indexOf(',') > -1 && text.indexOf('.') > -1) {
      text = text.replace(/\./g, '').replace(',', '.');
    } else if (text.indexOf(',') > -1) {
      // Só vírgula: usar como decimal
      text = text.replace(',', '.');
    }
    const parsed = parseFloat(text);
    return isNaN(parsed) ? 0 : parsed;
  }

  private toInt(value: any): number {
    const n = Math.round(this.toNumber(value));
    return isNaN(n) ? 0 : n;
  }

  /**
   * Garante que cada item do simulador esteja num formato numérico consistente
   */
  private sanitizarItem(item: any): any {
    // Peso: se parecer informado em gramas (ex.: 5000), converter para kg
    let weightKg = this.toNumber(item.weight);
    if (weightKg > 50 && weightKg <= 50000) {
      const converted = weightKg / 1000;
      // Apenas aplica se conversão resultar num valor plausível (<= 50kg)
      if (converted <= 50) {
        weightKg = converted;
      }
    }

    return {
      weight: weightKg,
      height: this.toInt(item.height),
      width: this.toInt(item.width),
      length: this.toInt(item.length),
      quantity: this.toInt(item.quantity),
      declaredValue: this.toNumber(item.declaredValue),
      indice: item.indice,
      initial: item.initial === true
    };
  }

  /**
   * Extrai e trata mensagem de erro da API de cálculo de frete.
   * Ex.: erro 400 com message contendo JSON encodado e string "ErroAvaliacao".
   */
  private extrairMensagemErroFrete(err: any): string {
    const fallback = 'Não foi possível calcular o frete. Verifique os dados dos produtos.';

    try {
      // Capturar mensagem crua de diferentes formatos do HttpErrorResponse
      let raw: any = '';
      if (err && typeof err.error === 'string') {
        raw = err.error; // corpo retornou como string
      } else if (err && err.error && typeof err.error.message === 'string') {
        raw = err.error.message;
      } else if (err && err.error && err.error.error && typeof err.error.error.message === 'string') {
        raw = err.error.error.message;
      } else if (err && typeof err.message === 'string') {
        raw = err.message; // fallback ("Http failure response for ...")
      }
      if (!raw) { return fallback; }

      // A API retorna um JSON serializado em string. Tentar parsear.
      let parsed: any = null;
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Se não for JSON válido, tratar como texto simples
      }

      const inner = parsed && parsed.error ? String(parsed.error) : String(raw);

      // Caso solicitado: exibir apenas o que vem após o marcador específico
      const marker = 'ErroAvaliacao(tipo=TipoErroAvaliacao.LIMITE_DE_PESO_MAX(descricao=';
      const markerIndex = inner.indexOf(marker);
      if (markerIndex !== -1) {
        const start = markerIndex + marker.length;
        let end = inner.indexOf('),', start);
        if (end === -1) {
          end = inner.indexOf(')', start);
        }
        const onlyDescription = inner.substring(start, end !== -1 ? end : inner.length).trim();
        if (onlyDescription) {
          return onlyDescription;
        }
      }

      // Tratar casos conhecidos: LIMITE_DE_PESO_MAX
      const pesoMatch = /LIMITE_DE_PESO_MAX[\s\S]*?pesoMaximo=([0-9.,]+)[\s\S]*?valorAvaliado=([0-9.,]+)/i.exec(inner);
      if (pesoMatch) {
        const max = this.toNumber(pesoMatch[1]);
        const avaliado = this.toNumber(pesoMatch[2]);
        return `Limite de peso excedido: máximo ${max.toFixed(2)} kg, avaliado ${avaliado.toFixed(2)} kg.`;
      }

      // Extrair descrição se existir, ex.: descricao=Limite de peso (kg) = 50.00
      const descMatch = /descricao=([^,)]+)/i.exec(inner);
      if (descMatch) {
        return descMatch[1];
      }

      // Fallback para exibir parte útil do inner
      return inner.replace(/^(\[|\])+|\]$/g, '').slice(0, 300);
    } catch {
      return fallback;
    }
  }
}
